/* curved-heading.js — responsive auto-fit for the brand curved headings.

   Rule (per design): keep a FIXED font size (desktop = the heading's `size`,
   mobile = half of it), grow the arc into whatever width is available, and when
   a line still won't fit at the widest allowed arc, WRAP it onto another arched
   row. Author line breaks (\n) are hard breaks; the fitter only adds breaks.

   Result: headings that already fit are rendered exactly as before (desktop arc
   = 700, mobile ≈ half); only overflowing headings widen, then wrap.

   Geometry ratios MIRROR snippets/curved-heading.liquid — keep the two in sync. */
(() => {
  const NS = 'http://www.w3.org/2000/svg';
  const MARGIN = 40;                 // side inset of the arc within the viewBox (units)
  const RISE = 0.5, GAP = 1.15, CAP = 0.78, DESC = 0.30;
  const FILL = 0.96;                 // fraction of a line's arc that text may fill
  const CAP_W = 900, BASE_W = 700;   // desktop max heading width / natural width (px == units, 1:1)

  const mk = (t) => document.createElementNS(NS, t);

  // Mixed-font runs: *asterisk*-wrapped text renders in the subheading font on the same arch.
  // parse "A *B* C" -> [{text:"A ",em:false},{text:"B",em:true},{text:" C",em:false}] (drops empties)
  const parseRuns = (line) => {
    const out = [];
    line.split('*').forEach((seg, i) => { if (seg !== '') out.push({ text: seg, em: i % 2 === 1 }); });
    return out;
  };
  // split a run's text into word objects carrying the run's em flag
  const runWords = (run) => run.text.split(/\s+/).filter(Boolean).map((t) => ({ text: t, em: run.em }));
  // fill an SVG text/textPath node with word tspans (single spaces between words)
  const fillWords = (node, words) => {
    while (node.firstChild) node.removeChild(node.firstChild);
    words.forEach((wd, i) => {
      const ts = mk('tspan');
      if (wd.em) ts.setAttribute('class', 'curved-heading__em');
      ts.textContent = (i ? ' ' : '') + wd.text;
      node.appendChild(ts);
    });
  };

  // hidden <text> reused to measure a word list's rendered length (user units) at a size.
  // Uses real tspans so mixed-font (Dioda + Gooper) runs measure with their true widths.
  function measure(svg, words, sizePx) {
    let t = svg.__m;
    if (!t) {
      t = mk('text');
      t.setAttribute('class', 'curved-heading__text');
      t.setAttribute('x', '-99999');
      t.setAttribute('y', '-99999');
      t.style.visibility = 'hidden';
      svg.appendChild(t);
      svg.__m = t;
    }
    t.style.fontSize = sizePx + 'px';
    fillWords(t, words);
    return t.getComputedTextLength();
  }

  function layout(svg) {
    const raw = svg.dataset.chText || '';
    if (!raw.trim()) return;
    const curve = svg.dataset.chCurve || 'arch';
    const dsk = parseFloat(svg.dataset.chSize) || 64;
    const uid = svg.dataset.chUid || 'ch';

    const vw = window.innerWidth;
    const mobile = vw <= 749;
    const size = mobile ? Math.round(dsk / 2) : dsk;   // fixed 64 / 32 (or half of a custom size)

    const host = svg.parentElement;
    const outer = host && host.parentElement ? host.parentElement.clientWidth
      : (host ? host.clientWidth : BASE_W);
    const maxPx = Math.min(outer || BASE_W, mobile ? vw - 16 : CAP_W);
    const lineCap = (maxPx - 2 * MARGIN) * FILL;       // max straight text per arched row

    // authored (hard) lines → wrap to fit lineCap, preferring breaks at *emphasis* run
    // boundaries so a two-font heading splits cleanly (each font on its own arched line)
    // instead of mid-phrase. A single run that alone overflows falls back to word-wrap.
    // Each display line is an array of {text, em} word objects.
    const hard = raw.split(/\r?\n/).map((s) => s.trim()).filter(Boolean);
    const lines = [];
    for (const hl of hard) {
      let cur = [];
      const wordWrap = (words) => {
        for (const wd of words) {
          const trial = cur.concat(wd);
          if (cur.length && measure(svg, trial, size) > lineCap) { lines.push(cur); cur = [wd]; }
          else cur = trial;
        }
      };
      for (const run of parseRuns(hl)) {
        const words = runWords(run);
        if (!words.length) continue;
        // if the whole run won't fit after the current content, break at the run boundary
        if (cur.length && measure(svg, cur.concat(words), size) > lineCap) { lines.push(cur); cur = []; }
        // add the run whole, or word-wrap it if it alone exceeds a line
        if (measure(svg, words, size) > lineCap) wordWrap(words);
        else cur = cur.concat(words);
      }
      if (cur.length) lines.push(cur);
    }
    if (!lines.length) return;

    let widest = 0;
    for (const ln of lines) widest = Math.max(widest, measure(svg, ln, size));

    // width: never below the natural width (700 desktop / half on mobile), never above the cap
    const lower = Math.min(BASE_W * size / dsk, maxPx);
    let vbw = Math.round(Math.min(Math.max(lower, widest / FILL + 2 * MARGIN), maxPx));

    // geometry (user units) — mirrors the Liquid
    const rise = size * RISE, gap = size * GAP, cap = size * CAP, desc = size * DESC;
    // 'arch' lifts the baseline (extra top pad); 'valley' & 'flat' keep the baseline low.
    const toppad = curve === 'arch' ? cap + rise : cap;
    const span = (lines.length - 1) * gap;
    const vbh = Math.round(curve === 'valley'
      ? toppad + span + 2 * rise + desc
      : toppad + span + desc);
    const xl = MARGIN, xr = vbw - MARGIN, xm = vbw / 2;

    // clear previous render (server SVG or a prior pass), keep the hidden measurer
    [...svg.querySelectorAll('text')].forEach((n) => { if (n !== svg.__m) n.remove(); });
    const oldDefs = svg.querySelector('defs');
    if (oldDefs) oldDefs.remove();

    const defs = mk('defs');
    const frag = document.createDocumentFragment();
    lines.forEach((ln, i) => {
      const edge = i * gap + toppad;
      const ctrl = curve === 'valley' ? edge + 2 * rise
        : curve === 'flat' ? edge
        : edge - 2 * rise;
      const p = mk('path');
      p.setAttribute('id', `ch-path-${uid}-${i}`);
      p.setAttribute('d', `M ${xl} ${edge} Q ${xm} ${ctrl} ${xr} ${edge}`);
      p.setAttribute('fill', 'none');
      defs.appendChild(p);

      const text = mk('text');
      text.setAttribute('text-anchor', 'middle');
      text.setAttribute('class', 'curved-heading__text');
      const tp = mk('textPath');
      tp.setAttribute('href', `#ch-path-${uid}-${i}`);
      tp.setAttribute('startOffset', '50%');
      fillWords(tp, ln);
      text.appendChild(tp);
      frag.appendChild(text);
    });
    svg.insertBefore(defs, svg.firstChild);
    svg.appendChild(frag);

    svg.setAttribute('viewBox', `0 0 ${vbw} ${vbh}`);
    svg.style.setProperty('--chs', size + 'px');
    svg.style.width = '100%';
    if (host) { host.style.maxWidth = vbw + 'px'; host.style.marginInline = 'auto'; }
    svg.dataset.chReady = '1';
  }

  function run() {
    document.querySelectorAll('svg.curved-heading[data-ch-text]').forEach(layout);
  }

  let raf = 0;
  function schedule() { cancelAnimationFrame(raf); raf = requestAnimationFrame(run); }

  function boot() {
    run();
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(run);
    window.addEventListener('resize', schedule, { passive: true });
    document.addEventListener('shopify:section:load', schedule);
    document.addEventListener('shopify:section:select', schedule);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
